import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Typography,
  Chip,
  Switch,
} from "@mui/material";
import { alpha } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Pagination } from "components/Pagination";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import StyledTableCell from "components/common/StyledTableCell";
import { formatDate } from "app/utils/dateUtils";
import ListActionButtons from "components/ActionButtons/ListActionButtons";
import { useSnackbar } from "notistack";
import LoadingView from "components/LoadingView";
import ErrorView from "components/ErrorView";
import { FIELD_TYPES } from "app/utils/constants/constants";

const GenericTable = ({
  entityConfig,
  fetchService,
  updateService,
  onBooleanToggleMessage = (row, fieldId, newValue) =>
    `Record's ${fieldId} has been ${newValue ? "enabled" : "disabled"}.`,
  page: initialPage = 1,
  pageSize: initialPageSize = 25,
  sortBy: initialSortBy = "id",
  sortOrder: initialSortOrder = "asc",
  onPageChange,
  onPageSizeChange,
  onSortChange,
  additionalFetchParams = {},
}) => {
  const navigate = useNavigate();
  const isAdmin = useAdmin();
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [orderBy, setOrderBy] = useState(initialSortBy);
  const [order, setOrder] = useState(initialSortOrder);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fields = useMemo(() => {
    return Object.entries(entityConfig.entityStructure.fields)
      .filter(([_, field]) => !field.isHiddenInTable)
      .map(([key, field]) => ({
        id: key,
        label: field.label || key.charAt(0).toUpperCase() + key.slice(1),
        type: field.type,
        labelMap: field.labelMap,
        colorMap: field.colorMap,
        render: field.render,
        listTableSticky: field.listTableSticky || false,
        listTableStickyLeft: field.listTableStickyLeft || 0,
        minWidth: field.minWidth || 100,
      }));
  }, [entityConfig.entityStructure.fields]);

  useEffect(() => {
    const fetchData = async () => {
      if (!fetchService || !entityConfig.backendApiPaths.getList) {
        console.error("Invalid fetchService or getList:", {
          fetchService,
          getList: entityConfig.backendApiPaths.getList,
        });
        setError("Invalid fetch service or API path");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const res = await fetchService(
          entityConfig.backendApiPaths.getList,
          page - 1,
          rowsPerPage,
          orderBy,
          order,
          additionalFetchParams
        );

        if (res.status !== 200) {
          throw new Error(
            `API error ${res.status}: ${res.statusText || "Request failed"}`
          );
        }

        const fetchedData = res.data;
        if (fetchedData.length === 0) {
          console.warn("No data returned in response");
        }

        setData(fetchedData);
        const total = Number(
          res.headers?.["x-total-count"] ||
            res.data?.total ||
            fetchedData.length ||
            0
        );
        setTotalCount(total);
      } catch (error) {
        console.error("Fetch error:", error);
        setData([]);
        setTotalCount(0);
        setError(error.message || "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    fetchService,
    entityConfig.backendApiPaths.getList,
    page,
    rowsPerPage,
    orderBy,
    order,
    additionalFetchParams,
  ]);

  const handleRequestSort = (columnId) => {
    setOrderBy((prevOrderBy) => {
      if (prevOrderBy === columnId) {
        setOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        return prevOrderBy;
      } else {
        setOrder("asc");
        return columnId;
      }
    });
    setPage(1);
    if (onSortChange) {
      onSortChange(columnId, order === "asc" ? "desc" : "asc");
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
    if (onPageSizeChange) {
      onPageSizeChange(+event.target.value);
    }
  };

  const handleView = (row) => {
    if (entityConfig.uiPaths.list?.path) {
      navigate(`${entityConfig.uiPaths.list.path}/${row.id}`);
    }
  };

  const handleUpdate = (row) => {
    if (entityConfig.uiPaths.update?.path) {
      navigate(entityConfig.uiPaths.update.path.replace(":id", row.id));
    }
  };

  const handleDelete = async (row) => {
    if (entityConfig.backendApiPaths.delete) {
      alert(`Delete ${row.login || row.id}?`);
    }
  };

  const handleBooleanToggle = async (row, fieldId) => {
    const originalValue = row[fieldId];
    const newValue = !originalValue;
    const updatedRow = { ...row, [fieldId]: newValue };

    setData((prevData) =>
      prevData.map((item) => (item.id === row.id ? updatedRow : item))
    );

    try {
      await updateService(
        entityConfig.backendApiPaths.update.replace(":id", row.id),
        updatedRow
      );
      enqueueSnackbar(onBooleanToggleMessage(row, fieldId, newValue), {
        variant: "success",
      });
    } catch (err) {
      setData((prevData) =>
        prevData.map((item) => (item.id === row.id ? row : item))
      );
      enqueueSnackbar(err.message || "Failed to update", { variant: "error" });
    }
  };

  const isAdminContext = entityConfig.uiPaths.list.path.startsWith("/admin");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <TableContainer
        sx={{ flexGrow: 1, overflowX: "auto", position: "relative" }}
      >
        {isLoading ? (
          <LoadingView message="Loading..." />
        ) : error ? (
          <ErrorView
            error={error}
            onBack={() => navigate(-1)}
            backButtonLabel="Back"
          />
        ) : data.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <Typography>No data found</Typography>
          </Box>
        ) : (
          <Table sx={{ minWidth: "max-content" }}>
            <TableHead>
              <TableRow>
                {fields.map((field) => (
                  <StyledTableCell
                    key={field.id}
                    sx={
                      isAdminContext && field.listTableSticky
                        ? {
                            position: "sticky",
                            left: field.listTableStickyLeft,
                            zIndex: 3,
                            backgroundColor: "background.paper",
                            minWidth: field.minWidth,
                          }
                        : { minWidth: field.minWidth }
                    }
                  >
                    <TableSortLabel
                      active={orderBy === field.id}
                      direction={order}
                      onClick={() => handleRequestSort(field.id)}
                    >
                      {field.label}
                    </TableSortLabel>
                  </StyledTableCell>
                ))}
                <StyledTableCell sx={{ minWidth: 120 }}></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow
                  key={row.id}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  sx={{
                    cursor: "pointer",
                    boxShadow:
                      hoveredRow === row.id
                        ? `0 3px 1px 0 ${alpha("#000", 0)}`
                        : "none",
                    transition: "box-shadow 0.2s",
                    "&:hover": {
                      bgcolor: "#F5F7FA",
                    },
                  }}
                  onClick={() =>
                    navigate(`${entityConfig.uiPaths.list.path}/${row.id}`)
                  }
                >
                  {fields.map((field) => (
                    <TableCell
                      key={field.id}
                      sx={
                        isAdminContext && field.listTableSticky
                          ? {
                              position: "sticky",
                              left: field.listTableStickyLeft,
                              zIndex: 2,
                              backgroundColor: "background.paper",
                              minWidth: field.minWidth,
                            }
                          : {
                              minWidth: field.minWidth,
                            }
                      }
                    >
                      {field.render ? (
                        field.render(row[field.id], row)
                      ) : field.type === FIELD_TYPES.ARRAY ? (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(row[field.id] || []).map((item, idx) => (
                            <Chip
                              key={idx}
                              label={
                                field.labelMap
                                  ? field.labelMap[item] || item
                                  : item
                              }
                              size="small"
                              color={
                                field.colorMap && field.colorMap[item]
                                  ? field.colorMap[item]
                                  : "primary"
                              }
                            />
                          ))}
                        </Box>
                      ) : field.type === FIELD_TYPES.DATE ? (
                        formatDate(row[field.id])
                      ) : field.type === FIELD_TYPES.BOOLEAN ? (
                        <Switch
                          checked={row[field.id] || false}
                          onChange={() => handleBooleanToggle(row, field.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!isAdmin || !updateService}
                        />
                      ) : field.type === FIELD_TYPES.ENUM ? (
                        field.labelMap && row[field.id] ? (
                          <Chip
                            label={field.labelMap[row[field.id]]}
                            size="small"
                            color={
                              field.colorMap && field.colorMap[row[field.id]]
                                ? field.colorMap[row[field.id]]
                                : "primary"
                            }
                          />
                        ) : (
                          row[field.id] || "-"
                        )
                      ) : field.type === FIELD_TYPES.IMAGE ? (
                        row[field.id] ? (
                          <img
                            src={row[field.id]}
                            alt={field.label}
                            style={{ maxWidth: "50px", maxHeight: "50px" }}
                          />
                        ) : (
                          "-"
                        )
                      ) : field.type === FIELD_TYPES.OBJECT ? (
                        JSON.stringify(row[field.id]) || "-"
                      ) : (
                        row[field.id] || "-"
                      )}
                    </TableCell>
                  ))}
                  {isAdmin && (
                    <TableCell>
                      <ListActionButtons
                        row={row}
                        onView={handleView}
                        onEdit={
                          entityConfig.backendApiPaths.update
                            ? handleUpdate
                            : null
                        }
                        onDelete={
                          entityConfig.backendApiPaths.delete
                            ? handleDelete
                            : null
                        }
                        showEdit={!!entityConfig.backendApiPaths.update}
                        showDelete={!!entityConfig.backendApiPaths.delete}
                        isHovered={hoveredRow === row.id}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!isLoading && data.length > 0 && (
        <Pagination
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </Box>
  );
};

export default GenericTable;
